import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve } from '@angular/router';
import { AppState } from './app.reducer';
import { combineLatest, filter, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as fromUser from './store/user.actions';
import { userAutoLoginAction } from './store/user.actions';
import { User } from './models/user.model';
import { Actions, ofType } from '@ngrx/effects';
import { userSelector } from './store/user.selectors';
import { getStoredUser } from './store/storage.meta-reducer';
import { UploadStatus } from './models/upload-status.model';
import { APP_ORGANIZATION_CODE } from 'app/common';

@Injectable({
  providedIn: 'root'
})
export class UserResolverService implements Resolve<User | null> {

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    @Inject(APP_ORGANIZATION_CODE) private organizationCode: string
  ) {
  }

  resolve(route: ActivatedRouteSnapshot): Observable<User | null> {
    return combineLatest([
      this.store.select('user'),
      this.store.select('organizationInfo'),
    ]).pipe(
      // нам нужно дождаться, когда загрузится информация об организации. Это как раз нам помогает это сделать
      filter(
        ([, organizationInfo]) => !!organizationInfo.organizationInfo && !organizationInfo.isLoading
      ),
      take(1),
      switchMap(([userState]) => {

        let compiledUserState = {...userState};
        if (userState.organizationCode !== this.organizationCode) {
          // если коды не совпадают, то забираем данные текущей организации, авторизуем пользователя
          // и возвращаем его
          const cachedUser = getStoredUser(this.organizationCode);
          this.store.dispatch(userAutoLoginAction(cachedUser));
          compiledUserState = {...cachedUser};
        }
        if (compiledUserState.isAuthorized && compiledUserState.syncStatus === UploadStatus.NO) {
          // если пользователь авторизован и ещё не спарсил данные, то парсим их и возвращаем его
          this.store.dispatch(fromUser.userFetchingInfoAction());
          return this.actions$.pipe(
            ofType(...[
              fromUser.userSaveAction,
              fromUser.userLogoutAction
            ]),
            take(1),
            switchMap((value) => {
              return of(value.payload ?? null);
            })
          );
        } else {
          return this.store.select(userSelector).pipe(take(1));
        }
      })
    );
  }
}
